import i18n from '@/plugins/i18n/config.ts'
import {
  MandatePublicStatusEnum,
  MandateBoStatusEnum
} from "@/enums/mandate_enum.ts";
import {
  InvestmentStatusKey,
  WithdrawalStatusKey
} from "@/enums/bank_enum.ts";
import { LcbftClassificationEnum } from "@/enums/lcbft_enum.ts";
import { ReferralRewardStatusEnum } from "@/enums/referralReward_enum.ts";
import { IdentityDuplicateReviewStatusEnum } from "@/enums/identityDuplicateReview_enum.ts";

import type {
  MandateBoStatusKey,
  MandatePublicStatusKey,
} from '@/types/services/mandate/mandate.type.ts';
import type { UserStatusKey } from '@/types/services/user/user.type.ts';

const { t, te } = i18n.global

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return t('common.noData')
  return new Intl.DateTimeFormat('fr-FR', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(new Date(dateStr))
}

// Converts a Date to a pure "YYYY-MM-DD" string using local calendar fields (no timezone conversion).
// Use this before sending a date-only value (e.g. reportPeriod) to the API, so a UTC-serialized
// datetime (Date#toJSON) can't shift the calendar day depending on the browser's timezone.
export function toDateOnlyString(date: Date | string | null): string | null {
  if (!date) return null
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return null
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Formats a pure "YYYY-MM-DD" date-only string for display without going through Date/Intl
// timezone conversion (which would risk shifting the day back for negative UTC offsets).
// Falls back to formatDate for non date-only values.
export function formatDateOnly(dateStr: string | null): string {
  if (!dateStr) return t('common.noData')
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr)
  if (!match) return formatDate(dateStr)
  const [, year, month, day] = match
  return `${day}/${month}/${year}`
}

export function formatFullDate(dateStr: string | null): string {
  if (!dateStr) return t('common.noData')

  const date = new Date(dateStr)

  const datePart = new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)

  const timePart = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)

  return `${datePart} à ${timePart}`
}

export function formatCurrency(value: string | number | null): string {
  if (value == null) return t('common.noData')

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) / 100)
}

export function formatItemValue(value: string | null): string {
  return value ? value : t('common.noData')
}

export function formatMandatePublicStatus(status: MandatePublicStatusKey): string {
  switch (status) {
    case MandatePublicStatusEnum.OPENED:
      return 'green darken-2'
    case MandatePublicStatusEnum.PRIVATE:
      return 'blue darken-2'
    default:
      return 'red darken-2'
  }
}

export function formatMandateBoStatus(status: MandateBoStatusKey): string {
  switch (status) {
    case MandateBoStatusEnum.PENDING:
      return 'orange darken-2'
    case MandateBoStatusEnum.PENDING_DIC:
      return 'orange darken-2'
    case MandateBoStatusEnum.VALIDATED:
      return 'green darken-2'
    case MandateBoStatusEnum.REFUSED:
      return 'red lighten-1'
    default:
      // draft
      return 'grey darken-1'
  }
}

export function formatUserStatus(status: UserStatusKey): string {
  switch (status) {
    case 'inactive':
      return 'red darken-2'
    default:
      // draft
      return 'green darken-1'
  }
}

export function formatInvestmentStatus(status: InvestmentStatusKey): string {
  switch (status) {
    case InvestmentStatusKey.SIGNED:
      return 'blue darken-2'
    case InvestmentStatusKey.CANCELED:
      return 'red lighten-2'
    case InvestmentStatusKey.EXPIRED:
      return 'red lighten-2'
    case InvestmentStatusKey.PAYMENT_PENDING:
      return 'orange darken-2'
    case InvestmentStatusKey.VALIDATED:
      return 'green darken-2'
  }
}

export function formatWithdrawalStatus(status: WithdrawalStatusKey): string {
  switch (status) {
    case WithdrawalStatusKey.PENDING:
      return 'orange darken-2';
    case WithdrawalStatusKey.REFUSED:
      return 'red lighten-2';
    case WithdrawalStatusKey.VALIDATED:
      return 'green lighten-2';
  }
}

export function formatKycStatus(status: string | null): string {
  switch (status) {
    case 'approved':
      return 'green darken-2'
    case 'need_review':
      return 'orange darken-3'
    case 'rejected':
      return 'red lighten-2'
    default:
      return 'grey darken-1'
  }
}

export function formatKycState(status: string | null): string {
  switch (status) {
    case 'PROCESSED':
      return 'green darken-2'
    case 'VOID':
      return 'orange darken-2'
    case 'WAITING':
      return 'orange darken-2'
    case 'STARTED':
      return 'orange darken-2'
    case 'RUNNING':
      return 'orange darken-2'
    case 'FAILED':
      return 'red lighten-1'
    case 'ABORTED':
      return 'red lighten-1'
    case 'EXPIRED':
      return 'red lighten-1'
    case 'DELETED':
      return 'red lighten-1'
    default:
      return 'grey darken-1'
  }
}

export function formatPercentage(value: number | null): string {
  return Number(value).toFixed(2)
}

export function formatIban(value: string | null): string {
  if (!value) return t('common.noData')
  return value.replace(/(.{4})/g, '$1 ').trim();
}

export function formatLcbftClassification(value: string | null): string {
  switch (value) {
    case LcbftClassificationEnum.FAIBLE:
      return 'green darken-2'
    case LcbftClassificationEnum.MOYEN:
      return 'orange darken-2'
    case LcbftClassificationEnum.ELEVE:
      return 'red darken-2'
    case LcbftClassificationEnum.INTERDIT:
      return 'grey darken-4'
    default:
      return 'grey darken-1'
  }
}

// Dataleon KYC state/status/document values, LCB-FT enums, and the investor questionnaire's
// free-text `profile_type` all live under a namespaced i18n block keyed by the raw backend value
// (e.g. `views.detailUser.kycState.PROCESSED`). Dataleon can introduce new raw values on their
// side without a frontend release (see docs.dataleon.ai) — rather than hardcoding a closed list,
// this looks up a translation and falls back to the raw value itself when no label is defined.
export function formatKnownOrRaw(i18nKeyPrefix: string, value: string | null): string {
  if (!value) return t('common.noData')
  const key = `${i18nKeyPrefix}.${value}`
  return te(key) ? t(key) : value
}

// Amounts coming straight in euros (string or number), unlike `formatCurrency` which expects cents.
export function formatEuros(value: string | number | null): string {
  if (value == null) return t('common.noData')

  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value))
}

export function formatReferralRewardStatus(status: ReferralRewardStatusEnum): string {
  switch (status) {
    case ReferralRewardStatusEnum.PAID:
      return 'success'
    case ReferralRewardStatusEnum.REVIEW:
    case ReferralRewardStatusEnum.FAILED:
      return 'error'
    case ReferralRewardStatusEnum.PENDING:
    case ReferralRewardStatusEnum.PROCESSING:
      return 'warning'
    default:
      return 'grey'
  }
}

export function formatIdentityDuplicateReviewStatus(status: IdentityDuplicateReviewStatusEnum): string {
  switch (status) {
    case IdentityDuplicateReviewStatusEnum.OPEN:
      return 'warning'
    case IdentityDuplicateReviewStatusEnum.CONFIRMED:
      return 'error'
    default:
      return 'success'
  }
}
